import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui'
import ConnectionList from './ConnectionList'
import ConnectionRequestForm from './ConnectionRequestForm'

/**
 * CalendarConnections component for managing calendar connections with other users
 * A user can only have one calendar, which is either individual or shared
 */
const CalendarConnections = () => {
  const [connections, setConnections] = useState({ sent: [], received: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showConnectionForm, setShowConnectionForm] = useState(false)
  const { user } = useAuth()

  // Load connections from the API
  const loadConnections = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/calendar-connections/${user.email}`)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to load connections')
      }

      const data = await response.json()
      setConnections(data)
    } catch (err) {
      console.error('Error loading connections:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Load connections when the component mounts
  useEffect(() => {
    if (user) {
      loadConnections()
    }
  }, [user, loadConnections])

  // Check if the user has any active or pending connections
  const hasActiveOrPendingConnection = () => {
    const activeConnections = [
      ...connections.sent.filter(
        (c) => c.status === 'accepted' || c.status === 'pending'
      ),
      ...connections.received.filter(
        (c) => c.status === 'accepted' || c.status === 'pending'
      ),
    ]

    return activeConnections.length > 0
  }

  // Handle sending a connection request
  const handleSendRequest = async (targetEmail) => {
    try {
      setError(null)

      const response = await fetch('/api/calendar-connections/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: user.email,
          targetUserEmail: targetEmail,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to send connection request')
      }

      // Reload connections to show the new request
      await loadConnections()
      setShowConnectionForm(false)
    } catch (err) {
      console.error('Error sending connection request:', err)
      setError(err.message)
    }
  }

  // Handle accepting or rejecting a connection request
  const handleUpdateRequest = async (connectionId, status) => {
    try {
      setError(null)

      const response = await fetch(
        `/api/calendar-connections/${connectionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userEmail: user.email,
            status,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.error || `Failed to ${status} connection request`
        )
      }

      // Reload connections to reflect the updated status
      await loadConnections()
    } catch (err) {
      console.error(`Error ${status} connection request:`, err)
      setError(err.message)
    }
  }

  // Handle deleting a connection
  const handleDeleteConnection = async (connectionId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this connection? Your calendar will become individual again.'
      )
    ) {
      return
    }

    try {
      setError(null)

      const response = await fetch(
        `/api/calendar-connections/${connectionId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userEmail: user.email,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete connection')
      }

      // Reload connections
      await loadConnections()
    } catch (err) {
      console.error('Error deleting connection:', err)
      setError(err.message)
    }
  }

  if (!user) {
    return (
      <div className="p-4 text-center">
        Please sign in to manage calendar connections.
      </div>
    )
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow relative mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Calendar Connections</h2>
        {!hasActiveOrPendingConnection() && (
          <Button
            onClick={() => setShowConnectionForm(!showConnectionForm)}
            variant={showConnectionForm ? 'tertiary' : 'primary'}
            className="text-sm"
          >
            {showConnectionForm ? 'Cancel' : 'Connect with User'}
          </Button>
        )}
      </div>

      <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded">
        <p>
          When you connect with a user, both users will see the same calendar.
        </p>
        {hasActiveOrPendingConnection() ? (
          <p className="mt-2">
            To connect with a different user, delete your existing connection.
          </p>
        ) : (
          ''
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">{error}</div>
      )}

      {showConnectionForm && !hasActiveOrPendingConnection() && (
        <ConnectionRequestForm
          onSubmit={handleSendRequest}
          onCancel={() => setShowConnectionForm(false)}
        />
      )}

      {loading ? (
        <div className="p-4 text-center">Loading connections...</div>
      ) : (
        <>
          <ConnectionList
            connections={[
              ...connections.sent.filter((c) => c.status === 'accepted'),
              ...connections.received.filter((c) => c.status === 'accepted'),
            ]}
            userField={(connection) =>
              connection.connected_user ? 'connected_user' : 'user'
            }
            allowActions={false}
            onDelete={handleDeleteConnection}
            emptyMessage="No active connections."
          />

          {connections.received.filter((c) => c.status === 'pending').length >
            0 && (
            <ConnectionList
              title="Incoming Requests"
              connections={connections.received.filter(
                (c) => c.status === 'pending'
              )}
              userField="user"
              allowActions={true}
              onAccept={(id) => handleUpdateRequest(id, 'accepted')}
              onReject={(id) => handleUpdateRequest(id, 'rejected')}
              emptyMessage="No pending connection requests."
            />
          )}

          {connections.sent.filter((c) => c.status === 'pending').length >
            0 && (
            <ConnectionList
              title="Sent Requests"
              connections={connections.sent.filter(
                (c) => c.status === 'pending'
              )}
              userField="connected_user"
              allowActions={false}
              onDelete={handleDeleteConnection}
              emptyMessage="No pending sent requests."
            />
          )}
        </>
      )}
    </div>
  )
}

export default CalendarConnections
